import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import {
  ArrowLeft,
  Edit,
  FileDown,
  Trash2,
  User,
  Users,
  MapPin,
  Phone,
  Mail,
  Shield,
  Droplets,
  Camera,
  PenTool,
  Plus,
  Send,
  Briefcase,
  Target,
  ExternalLink,
  Copy,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { downloadClaimPdf, triggerBlobDownload, openPdfInNewTab } from "@/lib/pdfDownload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTY_B_ROLE_LABELS = {
  locataire: "Locataire ou occupant non propriétaire",
  proprietaire: "Propriétaire / copropriétaire",
  syndic: "Syndic",
  gerant: "Gérant de l'immeuble",
  autre: "Autre",
};

const PARTY_B_RELATION_LABELS = {
  "Appartement sous-jacent": "Appartement sous-jacent",
  "Appartement au-dessus": "Appartement au-dessus",
  "Appartement voisin": "Appartement voisin",
  "Local commercial voisin": "Local commercial voisin",
  "Parties communes": "Parties communes",
  "Autre": "Autre",
};

const ORIGIN_TYPE_LABELS = {
  canalisation_eau_froide: "Canalisation eau froide",
  canalisation_eau_chaude: "Canalisation eau chaude",
  evacuation: "Évacuation",
  joint_sanitaire: "Joint sanitaire",
  appareil_menager: "Appareil ménager",
  toiture: "Toiture",
  terrasse: "Terrasse",
  facade: "Façade",
  autre: "Autre",
};

const ORIGIN_LOCATION_LABELS = {
  partie_a: "Partie A",
  partie_b: "Partie B",
  parties_communes: "Parties communes",
  indeterminee: "Indéterminée",
};

const ClaimDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [pdfReady, setPdfReady] = useState(null);

  useEffect(() => {
    fetchClaim();
  }, [id]);

  const fetchClaim = async () => {
    try {
      const response = await axios.get(`${API}/claims/${id}`);
      setClaim(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement du dossier");
      navigate("/claims");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    setPdfReady(null);
    try {
      const { blobUrl, filename } = await downloadClaimPdf(id, claim?.file_number, token);
      triggerBlobDownload(blobUrl, filename);
      setPdfReady({ blobUrl, filename });
      toast.success("PDF téléchargé", {
        description: "Si le fichier ne s'ouvre pas, utilisez les options ci-dessous.",
      });
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenPdf = () => {
    if (pdfReady?.blobUrl) openPdfInNewTab(pdfReady.blobUrl);
  };

  const handleCopyPdfLink = () => {
    if (pdfReady?.blobUrl) {
      navigator.clipboard.writeText(pdfReady.blobUrl).then(() => toast.success("Lien copié")).catch(() => toast.error("Impossible de copier"));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/claims/${id}`);
      toast.success("Dossier supprimé");
      navigate("/claims");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // WorkOrders module disabled
  // const handleCreateWorkOrder = () => {
  //   navigate(`/work-orders?claim=${id}`);
  // };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!claim) {
    return null;
  }

  const buildingTypeLabels = {
    maison: "Maison",
    appartement: "Appartement",
    commerce: "Commerce",
    autre: "Autre",
  };

  const leakTypeLabels = {
    canalisation: "Canalisation",
    infiltration: "Infiltration",
    toiture: "Toiture",
    appareil: "Appareil",
    autre: "Autre",
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="claim-detail">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/claims")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">
                  {claim.file_number}
                </h1>
                <StatusBadge status={claim.status} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/claims/${id}/edit`)}
                className="h-10"
                data-testid="edit-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Client Info - Partie A */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-sky-600" />
              Partie A - Déclarant
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-lg font-semibold text-slate-900">
              {claim.client_first_name && `${claim.client_first_name} `}{claim.client_name}
            </div>
            {claim.client_phone && (
              <div className="flex items-center gap-2 text-slate-600">
                <Phone className="w-4 h-4" />
                <a href={`tel:${claim.client_phone}`} className="hover:text-sky-600">
                  {claim.client_phone}
                </a>
              </div>
            )}
            {claim.client_email && (
              <div className="flex items-center gap-2 text-slate-600">
                <Mail className="w-4 h-4" />
                <a href={`mailto:${claim.client_email}`} className="hover:text-sky-600">
                  {claim.client_email}
                </a>
              </div>
            )}
            {(claim.client_insurance || claim.client_contract_number) && (
              <div className="flex items-center gap-2 text-slate-600">
                <Shield className="w-4 h-4" />
                <span>
                  {claim.client_insurance}
                  {claim.client_contract_number && ` - N° ${claim.client_contract_number}`}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Party B Info (if exists) */}
        {(claim.party_b_name || claim.party_b_first_name) && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-500" />
                Partie B – Tiers concerné
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {claim.party_b_relation && (
                <div className="flex items-center gap-2 text-orange-600 bg-orange-50 px-3 py-2 rounded-lg">
                  <Briefcase className="w-4 h-4" />
                  <span className="font-medium">
                    {claim.party_b_relation === 'Autre' && claim.party_b_relation_other 
                      ? claim.party_b_relation_other 
                      : claim.party_b_relation}
                  </span>
                </div>
              )}
              {!claim.party_b_relation && claim.party_b_role && (
                <div className="flex items-center gap-2 text-orange-600 bg-orange-50 px-3 py-2 rounded-lg">
                  <Briefcase className="w-4 h-4" />
                  <span className="font-medium">
                    {claim.party_b_role === 'autre' && claim.party_b_role_other 
                      ? claim.party_b_role_other 
                      : PARTY_B_ROLE_LABELS[claim.party_b_role] || claim.party_b_role}
                  </span>
                </div>
              )}
              <div className="text-lg font-semibold text-slate-900">
                {claim.party_b_first_name && `${claim.party_b_first_name} `}{claim.party_b_name}
              </div>
              {claim.party_b_address && (
                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="w-4 h-4" />
                  <span>{claim.party_b_address}</span>
                </div>
              )}
              {claim.party_b_phone && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${claim.party_b_phone}`} className="hover:text-sky-600">
                    {claim.party_b_phone}
                  </a>
                </div>
              )}
              {claim.party_b_email && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${claim.party_b_email}`} className="hover:text-sky-600">
                    {claim.party_b_email}
                  </a>
                </div>
              )}
              {claim.party_b_piece_touchee && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Target className="w-4 h-4" />
                  <span>
                    Pièce touchée : <strong>
                      {claim.party_b_piece_touchee === 'Autre' && claim.party_b_piece_touchee_other 
                        ? claim.party_b_piece_touchee_other 
                        : claim.party_b_piece_touchee}
                    </strong>
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Address */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-sky-600" />
              Lieu du sinistre
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-900">{claim.address}</p>
            {(claim.postal_code || claim.city) && (
              <p className="text-slate-600">
                {claim.postal_code} {claim.city}
              </p>
            )}
            <p className="text-sm text-slate-500 mt-2">
              Type: {buildingTypeLabels[claim.building_type] || claim.building_type}
            </p>
          </CardContent>
        </Card>

        {/* Technical Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Droplets className="w-5 h-5 text-sky-600" />
              Informations techniques
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Origin section */}
            {(claim.origin_type || claim.origin_location) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 text-amber-800 font-medium">
                  <Target className="w-4 h-4" />
                  Origine du sinistre
                </div>
                {claim.origin_type && (
                  <div className="ml-6">
                    <span className="text-sm text-slate-500">Type : </span>
                    <span className="text-slate-900">
                      {ORIGIN_TYPE_LABELS[claim.origin_type] || claim.origin_type}
                    </span>
                  </div>
                )}
                {claim.origin_location && (
                  <div className="ml-6">
                    <span className="text-sm text-slate-500">Située chez : </span>
                    <span className="text-slate-900 font-medium">
                      {ORIGIN_LOCATION_LABELS[claim.origin_location] || claim.origin_location}
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {claim.leak_origin && (
              <div>
                <p className="text-sm text-slate-500">Description de l'origine</p>
                <p className="text-slate-900">{claim.leak_origin}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-slate-500">Type de fuite</p>
              <p className="text-slate-900">
                {leakTypeLabels[claim.leak_type] || claim.leak_type}
              </p>
            </div>
            {claim.affected_rooms?.length > 0 && (
              <div>
                <p className="text-sm text-slate-500">Pièces touchées</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {claim.affected_rooms.map((room) => (
                    <span
                      key={room}
                      className="px-2 py-1 bg-slate-100 rounded text-sm"
                    >
                      {room}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {claim.affected_materials?.length > 0 && (
              <div>
                <p className="text-sm text-slate-500">Matériaux impactés</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {claim.affected_materials.map((material) => (
                    <span
                      key={material}
                      className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-sm"
                    >
                      {material}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {claim.humidity_level && (
              <div>
                <p className="text-sm text-slate-500">Niveau d'humidité</p>
                <p className="text-slate-900">{claim.humidity_level}</p>
              </div>
            )}
            {claim.technical_observations && (
              <div>
                <p className="text-sm text-slate-500">Observations</p>
                <p className="text-slate-900 whitespace-pre-wrap">
                  {claim.technical_observations}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Photos */}
        {claim.photos?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Camera className="w-5 h-5 text-sky-600" />
                Photos ({claim.photos.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="photo-grid">
                {claim.photos.map((photo, index) => (
                  <div key={index} className="photo-item">
                    <img src={photo} alt={`Photo ${index + 1}`} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Signatures */}
        {(claim.client_signature || claim.technician_signature) && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <PenTool className="w-5 h-5 text-sky-600" />
                Signatures
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {claim.client_signature && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Client</p>
                    <img
                      src={claim.client_signature}
                      alt="Signature client"
                      className="border rounded p-2 bg-white max-h-24"
                    />
                  </div>
                )}
                {claim.technician_signature && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Technicien</p>
                    <img
                      src={claim.technician_signature}
                      alt="Signature technicien"
                      className="border rounded p-2 bg-white max-h-24"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Meta Info */}
        <Card>
          <CardContent className="py-4">
            <div className="flex justify-between text-sm text-slate-500">
              <span>
                Créé le {format(new Date(claim.created_at), "dd/MM/yyyy à HH:mm", { locale: fr })}
              </span>
              {claim.technician_name && (
                <span>Par {claim.technician_name}</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions PDF */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <Button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="h-14 bg-sky-600 hover:bg-sky-700"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <div className="spinner w-5 h-5 border-white border-t-transparent" />
              ) : (
                <>
                  <FileDown className="w-5 h-5 mr-2" />
                  Télécharger PDF
                </>
              )}
            </Button>
            
            <Button
              onClick={() => navigate(`/claims/${id}/send`)}
              className="h-14 bg-green-600 hover:bg-green-700"
              data-testid="send-email-btn"
            >
              <Send className="w-5 h-5 mr-2" />
              Envoyer par email
            </Button>
          </div>

          {pdfReady && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2" data-testid="pdf-fallback-actions">
              <p className="text-xs text-slate-500">
                Si le téléchargement ne démarre pas, vous pouvez :
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenPdf}
                  className="flex-1 text-xs"
                  data-testid="open-pdf-btn"
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  Ouvrir le PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyPdfLink}
                  className="flex-1 text-xs"
                  data-testid="copy-pdf-link-btn"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Copier le lien
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* WorkOrders button disabled — module unused
        <Button
          variant="outline"
          onClick={handleCreateWorkOrder}
          className="w-full h-12"
          data-testid="create-work-order-btn"
        >
          <Wrench className="w-5 h-5 mr-2" />
          Créer un ordre de travaux
        </Button>
        */}

        {user?.role === "company" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="w-full h-12 text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="delete-claim-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer le dossier
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                <AlertDialogDescription>
                  Êtes-vous sûr de vouloir supprimer ce dossier ? Cette action est irréversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </main>
    </div>
  );
};

export default ClaimDetail;
